// Analytics for the two hands-on flows: the payouts simulator and the business onboarding
// (welcome) flow. FullStory session capture and Segment events both start when one of those
// screens mounts — the rest of the portal (docs, reference) is never recorded or tracked.
//
// Event properties must never carry an API key, tax id, owner email or any other value typed
// into the onboarding forms.
import { FullStory, init, isInitialized } from '@fullstory/browser';
import { Analytics } from '@melio/platform-analytics';
import { useEffect } from 'react';

import { config } from '@/config';

type Properties = Record<string, unknown>;

export type TrackedFlow = 'simulator' | 'welcome' | 'onboarding';

let analyticsStarted = false;

function start() {
  if (!analyticsStarted) {
    Analytics.init(config.analytics);
    analyticsStarted = true;
  }
  if (config.fullStoryOrgId && !isInitialized()) {
    init({ orgId: config.fullStoryOrgId, devMode: config.environment === 'development' });
  }
}

export function trackEvent(flow: TrackedFlow, action: string, properties: Properties = {}) {
  const enriched = { environment: config.environment, ...properties };
  Analytics.getInstance().track(flow, action, enriched);
  if (isInitialized()) {
    FullStory('trackEvent', { name: `${flow} ${action}`, properties: enriched });
  }
}

// Boots analytics for the flows we track and tags the session with the flow it started on.
export function useFlowAnalytics(flow: TrackedFlow) {
  useEffect(() => {
    start();
    Analytics.getInstance().setViewContext(flow);
    if (isInitialized()) {
      FullStory('setProperties', { type: 'page', properties: { pageName: flow, environment: config.environment } });
    }
  }, [flow]);
}
